import classnames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useScreenshot } from 'use-react-screenshot';

import { Button } from '../Button/Button';
import { FileDropzone } from '../Forms/FileDropzone/FileDropzone.component';
import { SignatureDrawer } from '../SignatureDrawer/SignatureDrawer.component';
import { ToggleMenu, ToggleMenuItem } from '../ToggleMenu';

import styles from './SignatureWithChoice.module.scss';
import { Props, SIGNATURE_MODE } from './SignatureWithChoice.types';

const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

export const SignatureWithChoice = ({
  onClear,
  onSignChange,
  size,
  containerClassName,
  disabled,
  isMobile,
}: Props) => {
  const ref = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [signatureMode, setSignatureMode] = useState<SIGNATURE_MODE>(SIGNATURE_MODE.SIGN);
  const [downloadSign, setDownloadSign] = useState<string>();
  const [textSign, setTextSign] = useState('');
  const [image, takeScreenshot] = useScreenshot();

  useEffect(() => {
    if (signatureMode === SIGNATURE_MODE.TYPE && inputRef.current !== null) {
      inputRef.current.focus();
    }
  }, [signatureMode]);

  useEffect(() => {
    if (ref.current !== null && textSign !== '') {
      takeScreenshot(ref.current);
    } else if (ref.current !== null && textSign === '') {
      onClear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textSign]);
  useEffect(() => {
    onSignChange(image);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image]);

  const signatureComponent = useMemo(() => {
    switch (signatureMode) {
      case SIGNATURE_MODE.SIGN:
        return (
          <SignatureDrawer
            onClear={() => onClear()}
            onEnd={image => onSignChange(image)}
            size={size}
            disabled={disabled}
          />
        );
      case SIGNATURE_MODE.TYPE:
        return (
          <div className={styles.typeContainer}>
            <div className={styles.downloadImage} style={size}>
              <input
                disabled={disabled}
                value={textSign}
                ref={inputRef}
                onChange={e => {
                  setTextSign(e.currentTarget.value);
                }}
                className={styles.input}
              />
            </div>
            <div className={styles.header}>saisissez votre signature</div>
            <Button
              variant="tertiary"
              size="small"
              className={styles.clearButton}
              disabled={disabled}
              onClick={() => {
                setTextSign('');
                onClear?.();
              }}
            >
              effacer
            </Button>
            <div className={styles.copy}>
              <div className={styles.downloadImage} style={size} ref={ref}>
                <p className={styles.input}>{textSign}</p>
              </div>
            </div>
          </div>
        );
      case SIGNATURE_MODE.DOWNLOAD:
        return (
          <>
            {downloadSign ? (
              <div className={styles.typeContainer}>
                <div className={styles.downloadImage} style={size}>
                  <img
                    src={downloadSign}
                    alt={'sign'}
                    className={classnames(styles.image, { [styles.disabled]: disabled })}
                  />
                </div>
                <Button
                  variant="tertiary"
                  size="small"
                  className={styles.clearButton}
                  disabled={disabled}
                  onClick={() => {
                    setDownloadSign(undefined);
                    onClear();
                  }}
                >
                  effacer
                </Button>
              </div>
            ) : (
              <FileDropzone
                {...{
                  maxFiles: 1,
                  maxSize: 5000000,
                  accept: {
                    'image/*': ['.jpg', '.png', '.gif'],
                  },
                  advice: 'ajouter une photo de la signature',
                }}
                items={[]}
                onUpload={async items => {
                  if (items.length > 0) {
                    const fileAsString = await toBase64(items[0].file);
                    onSignChange(fileAsString as string);
                    setDownloadSign(fileAsString as string);
                  }
                }}
                onClick={() => 0}
                onDelete={() => 0}
                containerStyle={{
                  height: size?.height,
                  justifyContent: 'center',
                }}
                disabled={disabled}
              />
            )}
          </>
        );
    }
  }, [disabled, downloadSign, onClear, onSignChange, signatureMode, size, textSign]);

  return (
    <div
      className={classnames(styles.container, containerClassName)}
      style={{ width: size?.width }}
    >
      <ToggleMenu
        value={signatureMode}
        onChange={(item: SIGNATURE_MODE) => {
          onClear();
          setSignatureMode(item);
          if (item === SIGNATURE_MODE.DOWNLOAD) {
            setDownloadSign(undefined);
          } else if (item === SIGNATURE_MODE.TYPE) {
            setTextSign('');
          }
        }}
        className={styles.toggleMenu}
      >
        <ToggleMenuItem value={SIGNATURE_MODE.SIGN} disabled={disabled}>
          {isMobile ? 'manuelle' : 'signature manuelle'}
        </ToggleMenuItem>
        <ToggleMenuItem value={SIGNATURE_MODE.TYPE} disabled={disabled}>
          {isMobile ? 'texte' : 'saisie texte'}
        </ToggleMenuItem>
        <ToggleMenuItem value={SIGNATURE_MODE.DOWNLOAD} disabled={disabled}>
          téléchargement
        </ToggleMenuItem>
      </ToggleMenu>
      {signatureComponent}
    </div>
  );
};
